import { createContext, useCallback, type PropsWithChildren } from "react";

import { LoadingSlide } from "@components/title-slide-view";
import {
  DELETE_QUICK_FILTER,
  GET_QUICK_FILTERS,
  REORDER_QUICK_FILTERS,
  SYNC_QUICK_FILTERS,
  UPSERT_QUICK_FILTER,
} from "@gql/quick-filters";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type DeleteQuickFilter,
  type DeleteQuickFilterVariables,
  type GetQuickFilters,
  type GetQuickFiltersVariables,
  type ReorderQuickFilters,
  type ReorderQuickFiltersVariables,
  type SyncQuickFilters,
  type SyncQuickFiltersVariables,
  type UpsertQuickFilter,
  type UpsertQuickFilterVariables,
} from "@north-beam/nb-client-side";
import { type QuickFilterRevenueConfig } from "@north-beam/nb-common";

import { type QuickFiltersContextDescriptor } from "./types";

export const QuickFiltersContext = createContext<
  QuickFiltersContextDescriptor<QuickFilterRevenueConfig> | undefined
>(undefined);

interface Props {
  target: "revenue";
}

export const QuickFiltersProvider = ({
  target,
  children,
}: PropsWithChildren<Props>) => {
  const { data, loading } = useNorthbeamQuery<
    GetQuickFilters,
    GetQuickFiltersVariables
  >(GET_QUICK_FILTERS, {
    variables: { target },
  });

  const [upsertQuickFilterMutation, { loading: saving }] = useNorthbeamMutation<
    UpsertQuickFilter,
    UpsertQuickFilterVariables
  >(UPSERT_QUICK_FILTER, {
    refetchQueries: [{ query: GET_QUICK_FILTERS, variables: { target } }],
    awaitRefetchQueries: true,
  });

  const [reorderQuickFiltersMutation, { loading: reordering }] =
    useNorthbeamMutation<ReorderQuickFilters, ReorderQuickFiltersVariables>(
      REORDER_QUICK_FILTERS,
      {
        refetchQueries: [{ query: GET_QUICK_FILTERS, variables: { target } }],
        awaitRefetchQueries: true,
      },
    );

  const [deleteQuickFilterMutation, { loading: deleting }] =
    useNorthbeamMutation<DeleteQuickFilter, DeleteQuickFilterVariables>(
      DELETE_QUICK_FILTER,
      {
        refetchQueries: [{ query: GET_QUICK_FILTERS, variables: { target } }],
        awaitRefetchQueries: true,
      },
    );

  const [syncQuickFiltersMutation, { loading: syncing }] = useNorthbeamMutation<
    SyncQuickFilters,
    SyncQuickFiltersVariables
  >(SYNC_QUICK_FILTERS, {
    refetchQueries: [{ query: GET_QUICK_FILTERS, variables: { target } }],
    awaitRefetchQueries: true,
  });

  const upsertQuickFilter = useCallback(
    (variables: { id?: string; name: string; config: any }) => {
      return upsertQuickFilterMutation({ variables: { ...variables, target } });
    },
    [target],
  );

  const reorderQuickFilters = useCallback(
    (ids: string[]) => {
      return reorderQuickFiltersMutation({ variables: { ids, target } });
    },
    [target],
  );

  const deleteQuickFilter = useCallback(
    (id: string) => {
      return deleteQuickFilterMutation({ variables: { id, target } });
    },
    [target],
  );

  const syncQuickFilters = useCallback(() => {
    return syncQuickFiltersMutation({ variables: { target } });
  }, [target]);

  return (
    <QuickFiltersContext.Provider
      value={{
        quickFilters: data?.me.quickFilters ?? [],
        saving,
        reordering,
        deleting,
        syncing,
        upsertQuickFilter,
        reorderQuickFilters,
        deleteQuickFilter,
        syncQuickFilters,
      }}
    >
      {loading ? (
        <div className="h-[calc(100vh-140px)]">
          <LoadingSlide />
        </div>
      ) : (
        children
      )}
    </QuickFiltersContext.Provider>
  );
};
